import React, { useState, useRef, useEffect } from 'react';
import { X, Upload, Music, Image as ImageIcon, Play, Pause, Crown } from 'lucide-react';
import { uploadAudioToBunny, uploadCoverToBunny } from '../services/bunnyUpload';
import { createTrackWithUrls } from '../services/api';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function UploadTrackModal({ isOpen, onClose, onSuccess, categories, artists, loading, editingTrack = null }) {
  const [audioFile, setAudioFile] = useState(null);
  const [audioPreview, setAudioPreview] = useState(null);
  const [duration, setDuration] = useState('');
  const [coverImage, setCoverImage] = useState(null);
  const [coverPreview, setCoverPreview] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStatus, setUploadStatus] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [isPremium, setIsPremium] = useState(false);
  const [moodCategories, setMoodCategories] = useState([]);
  const [formData, setFormData] = useState({
    title: '',
    artist: '',
    artist_id: '',
    category_ids: [], // Catégories musique sélectionnées
    mood_category_ids: [], // Catégories mood sélectionnées
    cover_emoji: '🎵'
  });
  
  const audioRef = useRef(new Audio());
  const audioInputRef = useRef(null);
  const imageInputRef = useRef(null);

  // Charger les catégories de moods
  useEffect(() => {
    const loadMoodCategories = async () => {
      try {
        const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/admin/mood-categories/list`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          setMoodCategories(data);
        }
      } catch (error) {
        console.error('Erreur chargement mood categories:', error);
      }
    };
    if (isOpen) {
      loadMoodCategories();
    }
  }, [isOpen]);

  // Pré-remplir le formulaire si on modifie une piste existante
  useEffect(() => {
    if (editingTrack) {
      // Récupérer les catégories multiples si disponibles
      const trackCategoryIds = editingTrack.categories 
        ? editingTrack.categories.map(c => c.id.toString())
        : (editingTrack.category_id ? [editingTrack.category_id.toString()] : []);
      
      // Récupérer les mood categories si disponibles
      const trackMoodCategoryIds = editingTrack.mood_categories
        ? editingTrack.mood_categories.map(c => c.id.toString())
        : (editingTrack.mood_category_id ? [editingTrack.mood_category_id.toString()] : []);
      
      setFormData({
        title: editingTrack.title || '',
        artist: editingTrack.artist || '',
        artist_id: editingTrack.artist_id || '',
        category_ids: trackCategoryIds,
        mood_category_ids: trackMoodCategoryIds,
        cover_emoji: editingTrack.cover_emoji || '🎵'
      });
      setDuration(editingTrack.duration?.toString() || '');
      setIsPremium(editingTrack.is_premium === 1 || editingTrack.is_premium === true);
      if (editingTrack.cover_image) {
        setCoverPreview(editingTrack.cover_image);
      }
    } else {
      // Reset le formulaire pour une nouvelle piste
      setFormData({
        title: '',
        artist: '',
        artist_id: '',
        category_ids: [],
        mood_category_ids: [],
        cover_emoji: '🎵'
      });
      setDuration('');
      setIsPremium(false);
      setCoverPreview(null);
    }
  }, [editingTrack]);

  if (!isOpen) return null;

  const handleAudioChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setAudioFile(file);
    const url = URL.createObjectURL(file);
    setAudioPreview(url);

    // Pré-remplir le titre avec le nom du fichier audio (modale création uniquement)
    if (!editingTrack) {
      const name = file.name || '';
      const baseName = name.replace(/\.[^/.]+$/, '').trim();
      if (baseName) {
        setFormData((prev) => ({
          ...prev,
          title: prev.title ? prev.title : baseName
        }));
      }
    }

    // Charger l'audio pour obtenir la durée
    const audio = new Audio(url);
    audio.addEventListener('loadedmetadata', () => {
      const durationInSeconds = Math.round(audio.duration);
      setDuration(durationInSeconds.toString());
    });

    // Configurer l'audio ref pour la preview
    audioRef.current.src = url;
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setCoverImage(file);
    const url = URL.createObjectURL(file);
    setCoverPreview(url);
  };

  const toggleAudioPreview = () => {
    if (!audioPreview) return;

    if (isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
    } else {
      audioRef.current.play();
      setIsPlaying(true);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Pour une nouvelle piste, l'audio est obligatoire
    if (!editingTrack && !audioFile) {
      alert('Veuillez sélectionner un fichier audio');
      return;
    }

    const isMoodSelection = (formData.mood_category_ids || []).length > 0;
    // Validation artiste (facultatif si mood)
    if (!isMoodSelection && !formData.artist_id && !formData.artist) {
      alert('Veuillez sélectionner un artiste ou entrer un nom d\'artiste');
      return;
    }

    const title = formData.title;
    const artist = formData.artist;
    const artist_id = formData.artist_id;
    const category_ids = formData.category_ids;
    const mood_category_ids = formData.mood_category_ids;
    const cover_emoji = formData.cover_emoji || '🎵';
    
    // Première catégorie comme catégorie principale
    const category_id = category_ids.length > 0 ? category_ids[0] : null;
    // Première mood category comme mood_category_id principal
    const mood_category_id = mood_category_ids.length > 0 ? mood_category_ids[0] : null;
    // is_mood si au moins une catégorie mood est sélectionnée
    const is_mood = mood_category_ids.length > 0;

    setIsUploading(true);
    setUploadProgress(0);

    try {
      // Mode modification
      if (editingTrack) {
        setUploadStatus('💾 Mise à jour de la piste...');
        
        let coverUrl = editingTrack.cover_image;
        
        // Upload nouvelle cover si fournie
        if (coverImage) {
          setUploadStatus('🖼️ Upload nouvelle image de couverture...');
          coverUrl = await uploadCoverToBunny(coverImage, (progress) => {
            setUploadProgress(Math.round(progress * 0.5));
          });
        }
        
        setUploadProgress(60);
        setUploadStatus('💾 Enregistrement des modifications...');
        
        const token = localStorage.getItem('token');
        
        const response = await fetch(`${BACKEND_URL}/api/admin/tracks/${editingTrack.id}/update`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            title,
            artist: artist || '',
            artist_id: artist_id || null,
            duration: parseInt(duration),
            cover_image: coverUrl,
            category_id: category_id || null,
            category_ids: category_ids,
            mood_category_id: mood_category_id || null,
            mood_category_ids: mood_category_ids,
            cover_emoji,
            is_premium: isPremium,
            is_mood: is_mood
          })
        });
        
        if (!response.ok) {
          const error = await response.json();
          throw new Error(error.error || 'Erreur lors de la mise à jour');
        }
        
        setUploadProgress(100);
        setUploadStatus('✅ Piste mise à jour avec succès !');
      } else {
        // Mode création - upload vers Bunny CDN
        setUploadStatus('📤 Upload audio vers Bunny CDN...');

        // 1. Upload audio vers Bunny CDN
        const audioUrl = await uploadAudioToBunny(audioFile, (progress) => {
          setUploadProgress(Math.round(progress * 0.7));
        });

        // 2. Upload cover si présente
        let coverUrl = null;
        if (coverImage) {
          setUploadStatus('🖼️ Upload image de couverture...');
          coverUrl = await uploadCoverToBunny(coverImage, (progress) => {
            setUploadProgress(70 + Math.round(progress * 0.2));
          });
        }

        // 3. Envoyer les métadonnées au backend
        setUploadStatus('💾 Enregistrement en base de données...');
        setUploadProgress(90);
        
        await createTrackWithUrls({
          title,
          artist: artist || '',
          artist_id: artist_id || null,
          duration: parseInt(duration),
          file_path: audioUrl,
          cover_image: coverUrl,
          category_id: category_id || null,
          category_ids: category_ids,
          mood_category_id: mood_category_id || null,
          mood_category_ids: mood_category_ids,
          cover_emoji,
          is_premium: isPremium,
          is_mood: is_mood
        });

        setUploadProgress(100);
        setUploadStatus('✅ Piste ajoutée avec succès !');
      }
      
      // Reset on success
      setTimeout(() => {
        setAudioFile(null);
        setAudioPreview(null);
        setCoverImage(null);
        setCoverPreview(null);
        setDuration('');
        setIsPlaying(false);
        setUploadProgress(0);
        setUploadStatus('');
        setIsPremium(false);
        setFormData({
          title: '',
          artist: '',
          artist_id: '',
          category_ids: [],
          mood_category_ids: [],
          cover_emoji: '🎵'
        });
        if (audioInputRef.current) audioInputRef.current.value = '';
        if (imageInputRef.current) imageInputRef.current.value = '';
        
        if (onSuccess) onSuccess();
        onClose();
      }, 1000);
      
    } catch (error) {
      console.error('Upload error:', error);
      alert('Erreur: ' + (error.response?.data?.error || error.message));
    } finally {
      setIsUploading(false);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const toggleCategory = (catId, type) => {
    const catIdStr = catId.toString();
    const field = type === 'music' ? 'category_ids' : 'mood_category_ids';
    const currentIds = formData[field];
    
    if (currentIds.includes(catIdStr)) {
      setFormData({
        ...formData,
        [field]: currentIds.filter(id => id !== catIdStr)
      });
    } else {
      setFormData({
        ...formData,
        [field]: [...currentIds, catIdStr]
      });
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4 modal-text">
      <div className="bg-gradient-to-br from-gray-900 to-black border border-white/10 rounded-2xl w-full max-w-5xl p-8 relative max-h-[90vh] overflow-y-auto">
        
        {/* Upload Progress Overlay */}
        {isUploading && (
          <div className="absolute inset-0 bg-black/90 backdrop-blur-sm rounded-2xl z-10 flex flex-col items-center justify-center p-8">
            <div className="w-full max-w-md">
              {/* Animated icon */}
              <div className="flex justify-center mb-6">
                <div className="relative">
                  <div className="w-24 h-24 rounded-full border-4 border-white/10 flex items-center justify-center">
                    <Music size={40} className="text-green-500 animate-pulse" />
                  </div>
                  <svg className="absolute inset-0 w-24 h-24 -rotate-90">
                    <circle
                      cx="48"
                      cy="48"
                      r="44"
                      stroke="currentColor"
                      strokeWidth="4"
                      fill="none"
                      className="text-green-500"
                      strokeDasharray={276.46}
                      strokeDashoffset={276.46 - (276.46 * uploadProgress) / 100}
                      strokeLinecap="round"
                      style={{ transition: 'stroke-dashoffset 0.3s ease' }}
                    />
                  </svg>
                </div>
              </div>

              {/* Progress percentage */}
              <div className="text-center mb-4">
                <span className="text-5xl font-bold text-green-400">{uploadProgress}%</span>
              </div>

              {/* Progress bar */}
              <div className="w-full bg-white/10 rounded-full h-3 overflow-hidden mb-4">
                <div 
                  className="h-full bg-gradient-to-r from-green-500 to-green-400 rounded-full transition-all duration-300 ease-out"
                  style={{ width: `${uploadProgress}%` }}
                />
              </div>

              {/* File info */}
              <div className="text-center space-y-2">
                <p className="text-white font-medium truncate">
                  {audioFile?.name}
                </p>
                <p className="text-white/60 text-sm">
                  {audioFile && `${formatFileSize(audioFile.size * uploadProgress / 100)} / ${formatFileSize(audioFile.size)}`}
                </p>
                <p className="text-green-400 text-sm font-medium">
                  {uploadStatus}
                </p>
              </div>
            </div>
          </div>
        )}

        <div className="flex items-center justify-between mb-6">
          <h3 className="text-2xl font-bold">{editingTrack ? 'Modifier la piste' : 'Publier une musique'}</h3>
          <button
            onClick={onClose}
            disabled={isUploading}
            className="text-white/60 hover:text-white transition-colors disabled:opacity-50"
          >
            <X size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Colonne gauche - Fichiers */}
            <div className="space-y-6">
              {/* Audio File Upload - Masqué en mode édition */}
              {!editingTrack && (
                <div>
                  <label className="block text-sm font-medium mb-2">Fichier audio *</label>
                  <div className="space-y-3">
                    <input
                      ref={audioInputRef}
                      type="file"
                      accept="audio/*"
                      onChange={handleAudioChange}
                      className="hidden"
                      id="audio-input"
                      disabled={isUploading}
                    />
                    <label
                      htmlFor="audio-input"
                      className={`flex items-center justify-center gap-3 w-full px-4 py-6 bg-white/5 border-2 border-dashed border-white/20 rounded-lg cursor-pointer hover:bg-white/10 hover:border-white/30 transition-colors ${isUploading ? 'opacity-50 pointer-events-none' : ''}`}
                    >
                      <Upload size={24} />
                      <span>{audioFile ? audioFile.name : 'Cliquez pour sélectionner un fichier audio'}</span>
                    </label>

                    {audioPreview && (
                      <div className="bg-white/5 rounded-lg p-4 flex items-center gap-4">
                        <button
                          type="button"
                          onClick={toggleAudioPreview}
                          disabled={isUploading}
                          className="w-12 h-12 rounded-full bg-green-500 hover:bg-green-600 flex items-center justify-center text-black transition-colors disabled:opacity-50"
                        >
                          {isPlaying ? <Pause size={20} /> : <Play size={20} />}
                        </button>
                        <div className="flex-1">
                          <div className="text-sm font-medium">{audioFile?.name}</div>
                          <div className="text-xs text-white/60">
                            {audioFile && `Taille: ${formatFileSize(audioFile.size)}`}
                            {duration && ` • Durée: ${Math.floor(parseInt(duration) / 60)}:${(parseInt(duration) % 60).toString().padStart(2, '0')}`}
                          </div>
                        </div>
                        <Music size={24} className="text-white/40" />
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Cover Image Upload */}
              <div>
                <label className="block text-sm font-medium mb-2">Image de couverture</label>
                <div className="space-y-3">
                  <input
                    ref={imageInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleImageChange}
                    className="hidden"
                    id="cover-input"
                    disabled={isUploading}
                  />
                  <label
                    htmlFor="cover-input"
                    className={`flex items-center justify-center gap-3 w-full px-4 py-6 bg-white/5 border-2 border-dashed border-white/20 rounded-lg cursor-pointer hover:bg-white/10 hover:border-white/30 transition-colors ${isUploading ? 'opacity-50 pointer-events-none' : ''}`}
                  >
                    <ImageIcon size={24} />
                    <span>{coverImage ? coverImage.name : 'Cliquez pour sélectionner une image'}</span>
                  </label>

                  {coverPreview && (
                    <div className="bg-white/5 rounded-lg p-4">
                      <img
                        src={coverPreview}
                        alt="Aperçu"
                        className="w-full h-48 object-cover rounded-lg"
                      />
                    </div>
                  )}
                </div>
              </div>

              {/* Type: Gratuit/Premium */}
              <div>
                <label className="block text-sm font-medium mb-3">Accès</label>
                <div className="flex gap-4">
                  <label className={`flex items-center gap-3 px-4 py-3 rounded-lg cursor-pointer transition-all flex-1 ${!isPremium ? 'bg-green-500/20 border-2 border-green-500' : 'bg-white/5 border-2 border-white/10 hover:border-white/30'}`}>
                    <input
                      type="radio"
                      name="premium_type"
                      checked={!isPremium}
                      onChange={() => setIsPremium(false)}
                      disabled={isUploading}
                      className="sr-only"
                    />
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${!isPremium ? 'border-green-500 bg-green-500' : 'border-white/30'}`}>
                      {!isPremium && <div className="w-2 h-2 rounded-full bg-white" />}
                    </div>
                    <span className="font-medium">Gratuit</span>
                  </label>
                  
                  <label className={`flex items-center gap-3 px-4 py-3 rounded-lg cursor-pointer transition-all flex-1 ${isPremium ? 'bg-yellow-500/20 border-2 border-yellow-500' : 'bg-white/5 border-2 border-white/10 hover:border-white/30'}`}>
                    <input
                      type="radio"
                      name="premium_type"
                      checked={isPremium}
                      onChange={() => setIsPremium(true)}
                      disabled={isUploading}
                      className="sr-only"
                    />
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${isPremium ? 'border-yellow-500 bg-yellow-500' : 'border-white/30'}`}>
                      {isPremium && <div className="w-2 h-2 rounded-full bg-white" />}
                    </div>
                    <Crown size={18} className={isPremium ? 'text-yellow-500' : 'text-white/40'} />
                    <span className="font-medium">Premium</span>
                  </label>
                </div>
              </div>
            </div>

            {/* Colonne droite - Métadonnées */}
            <div className="space-y-4">
              {/* Title */}
              <div>
                <label className="block text-sm font-medium mb-2">Titre *</label>
                <input
                  type="text"
                  name="title"
                  required
                  value={formData.title}
                  onChange={(e) => setFormData({...formData, title: e.target.value})}
                  disabled={isUploading}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg focus:outline-none focus:border-green-500 disabled:opacity-50"
                  placeholder="Nom de la piste"
                />
              </div>

              {/* Artist Selection */}
              <div>
                <label className="block text-sm font-medium mb-2">Artiste *</label>
                <select
                  name="artist_id"
                  value={formData.artist_id}
                  onChange={(e) => setFormData({...formData, artist_id: e.target.value})}
                  disabled={isUploading}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg focus:outline-none focus:border-green-500 disabled:opacity-50"
                >
                  <option value="">Sélectionner un artiste</option>
                  {artists && artists.map(artist => (
                    <option key={artist.id} value={artist.id}>{artist.name}</option>
                  ))}
                </select>
              </div>

              {/* Artist Name (fallback) */}
              <div>
                <label className="block text-sm font-medium mb-2">Nom d'artiste (alternatif)</label>
                <input
                  type="text"
                  name="artist"
                  value={formData.artist}
                  onChange={(e) => setFormData({...formData, artist: e.target.value})}
                  disabled={isUploading}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg focus:outline-none focus:border-green-500 disabled:opacity-50"
                  placeholder="Si l'artiste n'est pas dans la liste"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                {/* Duration (auto-calculated) */}
                <div>
                  <label className="block text-sm font-medium mb-2">Durée (sec) *</label>
                  <input
                    type="number"
                    name="duration"
                    value={duration}
                    onChange={(e) => setDuration(e.target.value)}
                    required
                    readOnly={!!audioFile && !editingTrack}
                    disabled={isUploading}
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg focus:outline-none focus:border-green-500 disabled:opacity-50"
                    placeholder="Auto"
                  />
                </div>

                {/* Emoji */}
                <div>
                  <label className="block text-sm font-medium mb-2">Emoji</label>
                  <input
                    type="text"
                    name="cover_emoji"
                    placeholder="🎵"
                    value={formData.cover_emoji}
                    onChange={(e) => setFormData({...formData, cover_emoji: e.target.value})}
                    maxLength={4}
                    disabled={isUploading}
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg focus:outline-none focus:border-green-500 disabled:opacity-50"
                  />
                </div>
              </div>

              {/* Catégories Musique */}
              <div>
                <label className="block text-sm font-medium mb-2">
                  🎵 Catégories Musique
                  {formData.category_ids.length > 0 && (
                    <span className="ml-2 text-xs text-green-400">({formData.category_ids.length} sélectionnée{formData.category_ids.length > 1 ? 's' : ''})</span>
                  )}
                </label>
                <div className="bg-white/5 border border-white/10 rounded-lg p-3 max-h-32 overflow-y-auto">
                  {categories && categories.length > 0 ? (
                    <div className="grid grid-cols-2 gap-1">
                      {categories.map(cat => (
                        <label 
                          key={cat.id} 
                          className={`flex items-center gap-2 px-2 py-1.5 rounded cursor-pointer transition-all text-sm ${
                            formData.category_ids.includes(cat.id.toString()) 
                              ? 'bg-green-500/20 text-green-300' 
                              : 'hover:bg-white/5'
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={formData.category_ids.includes(cat.id.toString())}
                            onChange={() => toggleCategory(cat.id, 'music')}
                            disabled={isUploading}
                            className="w-3 h-3 rounded border-white/30 bg-white/10 text-green-500"
                          />
                          <span className="truncate">{cat.name}</span>
                        </label>
                      ))}
                    </div>
                  ) : (
                    <p className="text-white/40 text-sm text-center py-2">Aucune catégorie</p>
                  )}
                </div>
              </div>

              {/* Catégories Mood */}
              <div>
                <label className="block text-sm font-medium mb-2">
                   Catégories Mood
                  {formData.mood_category_ids.length > 0 && (
                    <span className="ml-2 text-xs text-purple-400">({formData.mood_category_ids.length} sélectionnée{formData.mood_category_ids.length > 1 ? 's' : ''})</span>
                  )}
                </label>
                <div className="bg-white/5 border border-purple-500/20 rounded-lg p-3 max-h-32 overflow-y-auto">
                  {moodCategories && moodCategories.length > 0 ? (
                    <div className="grid grid-cols-2 gap-1">
                      {moodCategories.map(cat => (
                        <label 
                          key={cat.id} 
                          className={`flex items-center gap-2 px-2 py-1.5 rounded cursor-pointer transition-all text-sm ${
                            formData.mood_category_ids.includes(cat.id.toString()) 
                              ? 'bg-purple-500/20 text-purple-300' 
                              : 'hover:bg-white/5'
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={formData.mood_category_ids.includes(cat.id.toString())}
                            onChange={() => toggleCategory(cat.id, 'mood')}
                            disabled={isUploading}
                            className="w-3 h-3 rounded border-white/30 bg-white/10 text-purple-500"
                          />
                          <span className="truncate">{cat.name}</span>
                        </label>
                      ))}
                    </div>
                  ) : (
                    <p className="text-white/40 text-sm text-center py-2">Aucune catégorie mood</p>
                  )}
                </div>
                <p className="text-xs text-white/40 mt-1">
                  Sélectionnez des catégories mood pour que cette musique apparaisse dans la section Moods
                </p>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-6 mt-6 border-t border-white/10">
            <button
              type="button"
              onClick={onClose}
              disabled={isUploading}
              className="flex-1 px-4 py-3 bg-white/10 rounded-full hover:bg-white/20 transition-colors disabled:opacity-50"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={loading || isUploading || (!editingTrack && !audioFile)}
              className="flex-1 px-4 py-3 bg-green-500 hover:bg-green-600 text-black font-semibold rounded-full disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {loading ? 'Traitement...' : editingTrack ? 'Mettre à jour' : 'Publier'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
